import React from 'react';
import { Feather } from '@expo/vector-icons';
import { Pressable, Text, View, StyleSheet } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Paprika } from '../../../../styles/theme';
import { ENTRY_META, EntryIcon } from '../entryMeta';
import type { MealEntry } from '../../../../redux/mealEntry';

interface Props {
  entry: MealEntry;
  mealLabel: string;
  title: string;
  sub: string;
  onTap: () => void;
  /** « + avec ça » — a note or leftovers take a side like a recipe dinner. */
  onAddSidePress?: () => void;
  testID?: string;
}

export function EntryRow({ entry, mealLabel, title, sub, onTap, onAddSidePress, testID }: Props) {
  const { t } = useTranslation();
  const meta = ENTRY_META[entry.type];
  const isSkip = entry.type === 'skip';

  return (
    <Pressable style={styles.row} onPress={onTap} testID={testID}>
      {/* Icon square */}
      <View
        style={[
          styles.iconBox,
          { backgroundColor: meta.tint },
          isSkip && styles.iconBoxSkip,
        ]}
      >
        <EntryIcon type={entry.type} size={18} color={meta.color} />
      </View>

      {/* Content */}
      <View style={styles.content}>
        <Text style={styles.kicker}>
          {mealLabel.toUpperCase()} · {t(meta.labelKey).toUpperCase()}
        </Text>
        <Text style={[styles.title, isSkip && styles.titleMuted]} numberOfLines={1}>
          {title}
        </Text>
        <View style={styles.subRow}>
          <Text style={styles.sub} numberOfLines={1}>{sub}</Text>
          {onAddSidePress && (
            <Pressable
              style={({ pressed }) => [styles.addSide, pressed && styles.addSidePressed]}
              onPress={(e) => {
                e.stopPropagation?.();
                onAddSidePress();
              }}
              accessibilityRole="button"
              accessibilityLabel={t('thisWeek.addSideLong')}
              testID="entry-add-side"
              hitSlop={6}
            >
              <Feather name="plus" size={11} color={Paprika.primary} />
              <Text style={styles.addSideText}>{t('thisWeek.addSide')}</Text>
            </Pressable>
          )}
        </View>
      </View>

      {/* Edit icon */}
      <View style={styles.editIcon}>
        <Feather name="edit-2" size={16} color={Paprika.textSecondary} />
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 10,
    paddingHorizontal: 16,
  },
  iconBox: {
    width: 42,
    height: 42,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  iconBoxSkip: {
    borderWidth: 1.5,
    borderStyle: 'dashed' as any,
    borderColor: Paprika.border,
  },
  content: {
    flex: 1,
    gap: 2,
  },
  kicker: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 10,
    letterSpacing: 0.6,
    color: Paprika.textMuted,
  },
  title: {
    fontFamily: 'Inter-Medium',
    fontSize: 13.5,
    lineHeight: 18,
    color: Paprika.textPrimary,
    fontWeight: '500' as const,
  },
  titleMuted: {
    color: Paprika.textMuted,
  },
  subRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  sub: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    color: Paprika.textSecondary,
    flexShrink: 1,
  },
  addSide: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingVertical: 2,
    paddingHorizontal: 8,
    borderRadius: 999,
    backgroundColor: Paprika.brandTint,
  },
  addSidePressed: {
    backgroundColor: Paprika.surfaceLow,
  },
  addSideText: {
    fontFamily: 'Inter-Medium',
    fontSize: 12,
    color: Paprika.primary,
  },
  editIcon: {
    width: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: Paprika.bgSunk,
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
});
